/**
 * Company: SPHMedia
 * Description: [Landscape video section component for the homepage]
 */

import { BrightcoveVideo } from "@blocks/BrightcoveVideo/BrightcoveVideo";
import useStickyInlinePlayer from "@blocks/LatestVideo/hooks/useStickyInlinePlayer";
import {
  BC_LANDSCAPE_PLAYLIST_ID,
  BC_PLAYLIST_PLAYER_ID,
} from "@helper/getEnvVariables";
import stickyController from "@helper/stickyController";
import getProcessedBCResponse from "@transformer/useBrightcoveResponse";
import type {
  LatestVideoResponse,
  VideoPlayerRef,
} from "@typings/Brightcove.d";
import { ReactElement, useCallback, useRef, useState } from "react";

import SectionHeadings from "./SectionHeadings";

/**
 * LandscapeVideoSection
 * Description: [Renders the landscape video section for the homepage]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function LandscapeVideoSection(): ReactElement {
  const [isMute, setIsMute] = useState(true);
  const [isSticky, setIsSticky] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<LatestVideoResponse | null>(
    null,
  );
  const [isPlaying, setIsPlaying] = useState(false);

  const playerParentRef = useRef<HTMLDivElement | null>(null);
  const player = useRef<VideoPlayerRef | null>(null);

  // Sync mute state with the player
  const syncMute = useCallback(() => {
    setIsMute(player.current?.muted() ?? true);
  }, []);

  // Sync current video state with the player
  const syncCurrentVideo = useCallback(() => {
    const media = player.current?.mediainfo;

    if (!media?.id) return;

    setCurrentVideo((prev) =>
      prev?.id === media.id ? prev : getProcessedBCResponse([media])[0],
    );
  }, []);

  // Sync play state with the player
  const syncPlayState = useCallback(() => {
    setIsPlaying(!player.current?.paused?.());
  }, []);

  // Handle player ready event to sync mute state and current video
  const handlePlayerReady = useCallback(
    (playerInstance: VideoPlayerRef) => {
      player.current = playerInstance;

      syncMute();
      playerInstance.on("volumechange", syncMute);

      syncCurrentVideo();
      playerInstance.on("loadstart", syncCurrentVideo);
      playerInstance.on("playing", syncPlayState);
      playerInstance.on("pause", syncPlayState);

      playerInstance.on("dispose", () => {
        playerInstance.off?.("volumechange", syncMute);
        playerInstance.off?.("loadstart", syncCurrentVideo);
        playerInstance.off?.("playing", syncPlayState);
        playerInstance.off?.("pause", syncPlayState);
      });
    },
    [syncMute, syncCurrentVideo, syncPlayState],
  );

  const playerId = "landscape-video-player";
  useStickyInlinePlayer({
    observerRef: playerParentRef,
    playerId,
    isMute,
    isPlaying,
    setIsSticky,
  });

  return (
    <>
      <div className="border-b-0 lg:border-b border-pink-300 pb-[20px]">
        <SectionHeadings headingText="காணொளிகள்" headingLink="/videos" />

        <div ref={playerParentRef} className="flex flex-col">
          <div className={isSticky ? "min-h-[420px] xl:min-h-[456px]" : ""}>
            <div
              className={
                isSticky ? "w-[520px] fixed bottom-4 right-4 z-50" : "w-full"
              }
            >
              <div className="relative">
                <BrightcoveVideo
                  videoId="landscape-playlist"
                  playerId={BC_PLAYLIST_PLAYER_ID}
                  playlistId={BC_LANDSCAPE_PLAYLIST_ID}
                  onPlayerReady={handlePlayerReady}
                />

                {isSticky && (
                  <div className="z-20 absolute top-0 right-0 flex items-center justify-center w-8 h-8.4 p-1 rounded-full bg-gray-2150">
                    <button
                      type="button"
                      aria-label="Close picture-in-picture"
                      onClick={() => {
                        setIsSticky(false);
                        stickyController.clear(playerId);
                      }}
                      className="text-white-100 text-base outline-none focus:outline-none"
                    >
                      X
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col mt-2.5">
            <h3 className="font-bold font-primary text-2sm text-gray-100">
              {currentVideo?.name}
            </h3>
            <div className="flex mt-2.5 items-center justify-between">
              <span className="font-medium text-gray-150 text-xs">
                {currentVideo?.published_at}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
